import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_DEFAULT_DESCRIPTION,
  SITE_DEFAULT_IMAGE,
  SITE_LOCALE,
  SITE_TWITTER,
  canonical,
  abs,
  orgSchema,
  websiteSchema,
  localBusinessSchema,
} from "@/lib/seo";

/**
 * SEO — drop on every page.
 * Props:
 *  - title        (string)  page-level title (will be suffixed with | Hopsy)
 *  - description  (string)  meta description (< 160 chars)
 *  - path         (string)  current route path (e.g. "/shop") — used for canonical
 *  - image        (string)  OG image URL (absolute or path)
 *  - type         (string)  og:type — "website" | "article" | "product"
 *  - keywords     (array)   meta keywords (legacy but harmless; also used by some AI crawlers)
 *  - noindex      (bool)    true to add robots noindex
 *  - jsonLd       (obj|arr) schema.org JSON-LD blocks for this page
 *  - includeGlobalSchema (bool, default true) emits Organization + WebSite sitewide
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  type = "website",
  keywords = [],
  noindex = false,
  jsonLd,
  includeGlobalSchema = true,
  article,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${SITE_DEFAULT_DESCRIPTION.split(".")[0]}`;
  const desc = description || SITE_DEFAULT_DESCRIPTION;
  const url = canonical(path);
  const img = abs(image || SITE_DEFAULT_IMAGE);
  const blocks = [];
  if (includeGlobalSchema) {
    blocks.push(orgSchema(), websiteSchema(), localBusinessSchema());
  }
  if (Array.isArray(jsonLd)) blocks.push(...jsonLd);
  else if (jsonLd) blocks.push(jsonLd);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={SITE_LOCALE} />

      {/* Article-specific */}
      {type === "article" && article?.published_at && (
        <meta property="article:published_time" content={article.published_at} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {type === "article" && article?.section && (
        <meta property="article:section" content={article.section} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD blocks */}
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
